import {useEffect, useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Button} from '@/components/ui/button'
import {Tabs} from '@/components/ui/tabs'
import * as FormTabs from '@/components/ipevr/fila-form-tabs'
import type {FilaFormState, FilaFormTab} from '@/components/ipevr/fila-form-tabs'
import {ndToValorKey, useUpdateFila, type Fila, type Matriz} from '@/hooks/sgsst/riesgos/use-ipevr'
import {translateApiError} from '@/lib/errors'
import {validateRequired} from '@/lib/validation'

/** Validates at submit, not on blur: the Peligro tab renders its own inline error
 * and its `set('descripcion')` only wires onChange. */
function validateDescripcion(raw: string): string | null {
  return validateRequired(raw)
}

function toForm(fila: Fila): FilaFormState {
  return {
    proceso: fila.proceso ?? '',
    zona: fila.zona ?? '',
    actividad: fila.actividad ?? '',
    tarea: fila.tarea ?? '',
    rutinaria: fila.rutinaria,
    clasificacion: fila.clasificacion,
    descripcion: fila.descripcion,
    efectos_posibles: fila.efectos_posibles ?? '',
    nd: ndToValorKey(fila.nd),
    ne: fila.ne !== null ? String(fila.ne) : '',
    nc: fila.nc !== null ? String(fila.nc) : '',
    area_id: fila.area_id ?? '',
    num_expuestos: fila.num_expuestos !== null ? String(fila.num_expuestos) : '',
    peor_consecuencia: fila.peor_consecuencia ?? '',
    requisito_legal: fila.requisito_legal,
  }
}

export function EditFilaDialog({
  businessId,
  matriz,
  fila,
  onClose,
}: {
  businessId: string
  matriz: Matriz
  fila: Fila | null
  onClose: () => void
}) {
  const {t} = useTranslation()
  const update = useUpdateFila(businessId)
  const [form, setForm] = useState<FilaFormState>(FormTabs.EMPTY_FILA_FORM)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<FilaFormTab>('contexto')
  const [descripcionError, setDescripcionError] = useState(false)

  // Keyed on the id, not the object: every matriz refetch yields a new `fila`,
  // and re-syncing on it would clobber an in-progress edit.
  const filaId = fila?.id ?? null
  useEffect(() => {
    if (fila) setForm(toForm(fila))
    setError(null)
    setDescripcionError(false)
    setActiveTab('contexto')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filaId])

  const set = FormTabs.makeFilaFormFieldSetter(setForm)

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!fila) return
    if (validateDescripcion(form.descripcion)) {
      setDescripcionError(true)
      setActiveTab(FormTabs.FIELD_TAB.descripcion)
      return
    }
    setDescripcionError(false)
    update.mutate(
      {filaId: fila.id, fields: FormTabs.filaFormToPayload(form)},
      {onSuccess: onClose, onError: (err) => setError(translateApiError(err))}
    )
  }

  const {nd: ndOptions, ne: neOptions, nc: ncOptions} = FormTabs.gtc45Options(matriz)
  const showSuggestion =
    fila &&
    form.area_id === (fila.area_id ?? '') &&
    fila.area_suggested_num_expuestos !== null &&
    String(fila.area_suggested_num_expuestos) !== form.num_expuestos

  const tabsWithErrors = new Set<FilaFormTab>(descripcionError ? [FormTabs.FIELD_TAB.descripcion] : [])

  return (
    <Dialog open={!!fila} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className="flex max-h-[85vh] max-w-2xl flex-col overflow-hidden"
        data-testid="ipevr-edit-fila-dialog">
        <DialogHeader>
          <DialogTitle>{t('ipevr.editFila.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col" noValidate>
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as FilaFormTab)}
            className="flex min-h-0 min-w-0 flex-1 flex-col">
            <FormTabs.FilaFormTabsList testId="edit-fila-tabs" tabsWithErrors={tabsWithErrors} />
            <div className="min-h-0 flex-1 overflow-y-auto py-4">
              <FormTabs.ContextoTabContent
                form={form}
                set={set}
                setForm={setForm}
                idPrefix="edit"
                testIdPrefix="edit-fila"
              />
              <FormTabs.PeligroTabContent
                form={form}
                set={set}
                setForm={setForm}
                idPrefix="edit"
                testIdPrefix="edit-fila"
                descripcionError={descripcionError}
              />
              <FormTabs.ControlesTabContent
                form={form}
                set={set}
                setForm={setForm}
                idPrefix="edit"
                testIdPrefix="edit-fila"
                matrizAreas={matriz.areas}
                businessId={businessId}
                fila={fila ?? undefined}
              />
              <FormTabs.EvaluacionTabContent
                form={form}
                set={set}
                idPrefix="edit"
                testIdPrefix="edit-fila"
                ndOptions={ndOptions}
                neOptions={neOptions}
                ncOptions={ncOptions}
                numExpuestosSuggestion={
                  showSuggestion &&
                  fila && (
                    <button
                      type="button"
                      className="text-xs text-primary underline decoration-dotted"
                      data-testid="edit-fila-num-expuestos-suggestion"
                      onClick={() =>
                        setForm((f) => ({...f, num_expuestos: String(fila.area_suggested_num_expuestos)}))
                      }>
                      {t('ipevr.grid.numExpuestosSuggestion', {count: fila.area_suggested_num_expuestos})}
                    </button>
                  )
                }
              />
            </div>
          </Tabs>

          <div className="space-y-2 border-t border-border pt-3">
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {t('ipevr.editFila.submit')}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
